import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { getUserId, getUserDetails, getUserPackages, getUserPackageDetails, registerUser, upgradePackage, retopupPackage } from '../utils/contract';
import { fromWei } from '../utils/web3';
import { PACKAGE_NAMES, PACKAGE_PRICES, DEFAULT_REFERRER } from '../config/constants';

import { sendTelegramNotification } from '../utils/telegram';
import { CoreIcon, CORE_COLORS } from './CoreIcons';
import './Units.css';
const PACKAGE_SHORT_NAMES = ['NeuroCore', 'AlgoCore', 'IntelliCore', 'SynapCore', 'DeepCore', 'DataCore', 'MindCore', 'QuantumCore'];

function Units({ account }) {
  const location = useLocation();
  const [userDetails, setUserDetails] = useState(null);
  const [packages, setPackages] = useState([]);
  const [packageDetails, setPackageDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [referrerId, setReferrerId] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const windowParams = new URLSearchParams(window.location.search);
    const refParam = params.get('ref') || windowParams.get('ref');
    
    if (refParam) {
      setReferrerId(refParam);
      localStorage.setItem('referrerId', refParam);
    } else {
      const storedRef = localStorage.getItem('referrerId');
      if (storedRef) {
        setReferrerId(storedRef);
      }
    }
  }, [location]);

  useEffect(() => {
    if (showRegisterModal && !referrerId) {
      const params = new URLSearchParams(window.location.search);
      const refParam = params.get('ref');
      const storedRef = localStorage.getItem('referrerId');
      
      if (refParam) {
        setReferrerId(refParam);
        localStorage.setItem('referrerId', refParam);
      } else if (storedRef) {
        setReferrerId(storedRef);
      }
    }
  }, [showRegisterModal]);

  useEffect(() => {
    if (account) {
      loadUserData();
    }
  }, [account]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      let userId = await getUserId(account);
      
      if ((!userId || userId === '0')) {
        await new Promise(resolve => setTimeout(resolve, 200));
        userId = await getUserId(account);
      }
      
      if (userId && userId !== '0') {
        // BATCH 1: Load user details and packages in parallel
        const [details, pkgs] = await Promise.all([
          getUserDetails(userId),
          getUserPackages(userId)
        ]);
        
        setUserDetails(details);
        setPackages(pkgs);

        // BATCH 2: Load all active package details in parallel
        const activePackageIds = [];
        for (let i = 1; i <= 8; i++) {
          if (pkgs[i - 1]) {
            activePackageIds.push(i);
          }
        }
        
        if (activePackageIds.length > 0) {
          const detailPromises = activePackageIds.map(pkgId =>
            getUserPackageDetails(userId, pkgId).then(detail => ({ pkgId, detail }))
          );
          const detailResults = await Promise.all(detailPromises);
          
          const pkgDetails = {};
          detailResults.forEach(({ pkgId, detail }) => {
            pkgDetails[pkgId] = detail;
          });
          setPackageDetails(pkgDetails);
        }
        
        localStorage.removeItem('referrerId');
      } else {
        setShowRegisterModal(true);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setActionLoading('register');
    setMessage(null);
    try {
      const refId = referrerId.trim() || DEFAULT_REFERRER.toString();
      const tx = await registerUser(refId, account);
      setMessage({ type: 'success', text: '🎉 Welcome to Smartcore!' });
      setShowRegisterModal(false);
      localStorage.removeItem('referrerId');
      
      // Send Telegram notification
      sendTelegramNotification('register', {
        userId: 'New',
        package: PACKAGE_SHORT_NAMES[0],
        amount: fromWei(PACKAGE_PRICES[0]),
        txHash: tx.hash
      });
      
      setTimeout(() => loadUserData(), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpgrade = async (packageId) => {
    setActionLoading(`upgrade-${packageId}`);
    setMessage(null);
    try {
      const tx = await upgradePackage(packageId, account);
      setMessage({ type: 'success', text: `🚀 ${PACKAGE_SHORT_NAMES[packageId - 1]} activated!` });
      
      // Send Telegram notification
      sendTelegramNotification('upgrade', {
        package: PACKAGE_SHORT_NAMES[packageId - 1],
        amount: fromWei(PACKAGE_PRICES[packageId - 1]),
        txHash: tx.hash
      });
      
      setTimeout(() => loadUserData(), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetopup = async (packageId) => {
    setActionLoading(`retopup-${packageId}`);
    setMessage(null);
    try {
      const tx = await retopupPackage(packageId, account);
      setMessage({ type: 'success', text: `💎 ${PACKAGE_SHORT_NAMES[packageId - 1]} retopped up!` });
      
      // Send Telegram notification
      sendTelegramNotification('retopup', {
        package: PACKAGE_SHORT_NAMES[packageId - 1],
        amount: fromWei(PACKAGE_PRICES[packageId - 1]),
        txHash: tx.hash
      });
      
      setTimeout(() => loadUserData(), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(null);
    }
  };

  const getProgress = (earned, limit) => {
    if (!earned || !limit || limit === '0') return 0;
    return (parseFloat(fromWei(earned)) / parseFloat(fromWei(limit))) * 100;
  };

  // Helper function to format limit with unlimited check
  const formatLimit = (limit) => {
    const limitValue = parseFloat(fromWei(limit));
    // If limit is more than 6 digits (1000000 or more), show "Unlimited"
    if (limitValue >= 1000000) {
      return 'Unlimited';
    }
    return `${limitValue.toFixed(2)} USDT`;
  };

  const isUnlimited = (limit) => {
    const limitValue = parseFloat(fromWei(limit));
    return limitValue >= 1000000;
  };

  if (!account) {
    return (
      <div className="container">
        <div className="hero">
          <h1 className="hero-title">🤖 AI Core Systems</h1>
          <p className="hero-subtitle">Activate neural core systems and earn rewards</p>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ fontSize: '18px', color: 'var(--text-secondary)' }}>
            Connect your wallet to manage AI core systems
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-screen">
          <img src="/t1.gif" alt="Loading" style={{ width: '120px', height: '120px' }} />
          <div className="loading"></div>
          <div className="loading-text">Loading core systems...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Message Alert */}
      {message && createPortal(
        <div className={`hc-toast ${message.type}`}>
          <span className="hc-toast-icon">
            {message.type === 'success' ? '✓' : message.type === 'error' ? '!' : 'i'}
          </span>
          <span className="hc-toast-text">{message.text}</span>
        </div>,
        document.body
      )}

      {/* Hero Section */}
      <header className="page-hero">
        <div className="page-hero-text">
          <span className="page-hero-eyebrow">Smartcore</span>
          <h1 className="page-hero-title">AI Core Systems</h1>
          <p className="page-hero-sub">Manage and upgrade your neural core network</p>
        </div>
        {userDetails && (
          <div className="page-hero-id">
            <span>Core ID</span>
            <b>#{userDetails.id}</b>
          </div>
        )}
      </header>

      {/* Registration Modal */}
      {showRegisterModal && (
        <div className="modal-overlay" onClick={() => setShowRegisterModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src="/t1.gif" alt="AI" style={{ width: '32px', height: '32px' }} />
                Join Smartcore Network
              </h2>
              <button className="modal-close" onClick={() => setShowRegisterModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Initialize your neural core and start earning!
              </p>
              
              <div className="input-group">
                <label style={{ color: 'var(--text-cyan)' }}>Referrer ID</label>
                <input 
                  type="text" 
                  placeholder={`Enter referrer ID (default: ${DEFAULT_REFERRER})`}
                  value={referrerId} 
                  onChange={(e) => setReferrerId(e.target.value)} 
                />
                {referrerId && (
                  <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '5px' }}>
                    ✓ Referrer ID: {referrerId}
                  </small>
                )}
              </div>
              <div className="highlight-box">
                <p style={{ color: 'var(--text-cyan)', fontWeight: 'bold' }}>
                  🧠 Registration: 10 USDT (NeuroCore)
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '8px' }}>
                  Start with NeuroCore system and unlock the AI network
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRegisterModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleRegister} disabled={actionLoading === 'register'}>
                {actionLoading === 'register' ? (
                  <>
                    <div className="loading" style={{ display: 'inline-block', width: '16px', height: '16px', marginRight: '8px' }}></div> 
                    Initializing...
                  </>
                ) : (
                  <>🚀 Initialize NeuroCore</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Package Grid */}
      <div className="u-grid">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((pkgId) => {
          const isActive = packages[pkgId - 1];
          const details = packageDetails[pkgId];
          const canUpgrade = pkgId === 1 || (pkgId > 1 && packages[pkgId - 2]);
          const progress = details ? getProgress(details.totalEarned, details.ceilingLimit) : 0;
          const unlimitedLimit = details && isUnlimited(details.ceilingLimit);
          const locked = !isActive && !canUpgrade;

          return (
            <div
              key={pkgId}
              className={`u-card ${isActive ? 'active' : ''} ${locked ? 'locked' : ''}`}
              style={{ '--ca': CORE_COLORS[pkgId - 1] }}
            >
              <div className="u-card-head">
                <div className="u-gem"><CoreIcon index={pkgId - 1} /></div>
                <div className="u-meta">
                  <span className="u-name">{PACKAGE_SHORT_NAMES[pkgId - 1]}</span>
                  <span className="u-price">{fromWei(PACKAGE_PRICES[pkgId - 1])} <small>USDT</small></span>
                </div>
                {isActive && <span className="u-badge">● ACTIVE</span>}
                {locked && <span className="u-lock">🔒</span>}
              </div>

              {isActive && details ? (
                <>
                  <div className="u-stats">
                    <div className="u-stat"><span>Invested</span><b>{fromWei(details.totalInvested)}</b></div>
                    <div className="u-stat"><span>Earned</span><b>{fromWei(details.totalEarned)}</b></div>
                    <div className="u-stat"><span>Limit</span><b className={unlimitedLimit ? 'inf' : ''}>{formatLimit(details.ceilingLimit)}</b></div>
                    <div className="u-stat"><span>Remaining</span><b className={unlimitedLimit ? 'inf' : ''}>{unlimitedLimit ? 'Unlimited' : `${fromWei(details.remainingLimit)}`}</b></div>
                  </div>

                  {!unlimitedLimit && (
                    <div className="u-progress">
                      <div className="u-progress-top">
                        <span>Ceiling Progress</span>
                        <span>{progress.toFixed(1)}%</span>
                      </div>
                      <div className="u-progress-bar">
                        <div className="u-progress-fill" style={{ width: `${Math.min(progress, 100)}%` }}></div>
                      </div>
                    </div>
                  )}

                  <button className="u-btn retopup" onClick={() => handleRetopup(pkgId)} disabled={!!actionLoading}>
                    {actionLoading === `retopup-${pkgId}`
                      ? <><span className="u-spin" /> Processing...</>
                      : <>🔄 Retopup Core</>}
                  </button>
                </>
              ) : (
                canUpgrade && (
                  <button
                    className="u-btn activate"
                    onClick={() => pkgId === 1 ? setShowRegisterModal(true) : handleUpgrade(pkgId)}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === `upgrade-${pkgId}`
                      ? <><span className="u-spin" /> Activating...</>
                      : <>🚀 Activate Core</>}
                  </button>
                )
              )}

              {locked && (
                <div className="u-locked-msg">🔒 Activate previous core first</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Units;